/**
 * =============================================================================
 * FaqAccordion
 * Versão: 1.1.0
 *
 * Descrição:
 *   Componente reutilizável de accordion no estilo "FAQ" usado tanto na
 *   página de Planos (seção "Dúvidas Frequentes") quanto no modal de
 *   atividades (seção "Objeções comuns" do step de cadência).
 *
 *   Suporta dois formatos de resposta:
 *     - `answer`: ReactNode (texto comum) — uso da página de Planos
 *     - `answerHtml`: string HTML já sanitizada — uso das objeções
 *       (DOMPurify aplicado pelo chamador antes de passar para cá).
 *
 * Release notes:
 *   v1.1.0 - Adiciona prop `copyable` que exibe botão de cópia no cabeçalho
 *            de cada item, copiando o texto puro da resposta (extraído do
 *            HTML quando necessário). Utilizado nas Objeções comuns.
 *   v1.0.0 - Extração do FAQSection inline da página de Planos para
 *            componente compartilhado, reutilizável nas Objeções do
 *            ExecuteStepBody.
 * =============================================================================
 */
import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

export type FaqItem = {
  /** Identificador estável do item (opcional — fallback para índice) */
  id?: string;
  /** Pergunta exibida no trigger */
  question: string;
  /** Resposta como ReactNode (texto/markup pronto) */
  answer?: React.ReactNode;
  /** Resposta como HTML já sanitizado (renderizado via dangerouslySetInnerHTML) */
  answerHtml?: string;
  /** Destaca o item com borda/bg primary */
  highlight?: boolean;
  /** Badge opcional ao lado da pergunta (ex.: "Novo") */
  badge?: string;
};

export type FaqAccordionProps = {
  items: FaqItem[];
  /** 'single' (collapsible) ou 'multiple'. Padrão: 'single' */
  type?: 'single' | 'multiple';
  /** Exibe botão de copiar a resposta em cada item */
  copyable?: boolean;
  className?: string;
  itemClassName?: string;
};

/**
 * Extrai o texto puro de uma string HTML — usado para copiar a resposta das
 * objeções para a área de transferência sem tags/entidades.
 */
function htmlToPlainText(html: string): string {
  if (typeof window === 'undefined') return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
}

export const FaqAccordion: React.FC<FaqAccordionProps> = ({
  items,
  type = 'single',
  copyable = false,
  className,
  itemClassName,
}) => {
  const { toast } = useToast();
  // Guarda o id do último item copiado para trocar o ícone por 2s
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  const handleCopy = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast({ title: 'Resposta copiada' });
      window.setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 2000);
    } catch {
      toast({ title: 'Não foi possível copiar', variant: 'destructive' });
    }
  };

  // Renderiza o accordion respeitando o tipo (single requer collapsible)
  const content = items.map((item, index) => {
    const value = item.id ?? `faq-${index}`;
    const plain = item.answerHtml
      ? htmlToPlainText(item.answerHtml)
      : typeof item.answer === 'string'
        ? item.answer
        : '';
    return (
      <AccordionItem
        key={value}
        value={value}
        className={cn(
          'rounded-xl border px-6 data-[state=open]:shadow-sm',
          item.highlight ? 'border-primary/50 bg-primary/5' : 'bg-card',
          itemClassName
        )}
      >
        <div className="flex items-center gap-2">
          <AccordionTrigger className="flex-1 text-left text-base font-medium hover:no-underline">
            <div className="flex items-center gap-2">
              {item.question}
              {item.badge && (
                <span className="inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">
                  {item.badge}
                </span>
              )}
            </div>
          </AccordionTrigger>
          {copyable && plain && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={(e) => {
                // Evita alternar o accordion ao clicar em copiar
                e.stopPropagation();
                handleCopy(value, plain);
              }}
              title={copiedId === value ? 'Copiado' : 'Copiar resposta'}
            >
              {copiedId === value ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
        </div>
        <AccordionContent className="text-muted-foreground leading-relaxed">
          {item.answerHtml ? (
            <div
              className="prose prose-sm max-w-none"
              // HTML já sanitizado pelo chamador (DOMPurify)
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: item.answerHtml }}
            />
          ) : (
            item.answer
          )}
        </AccordionContent>
      </AccordionItem>
    );
  });

  if (type === 'multiple') {
    return (
      <Accordion type="multiple" className={cn('space-y-3', className)}>
        {content}
      </Accordion>
    );
  }

  return (
    <Accordion type="single" collapsible className={cn('space-y-3', className)}>
      {content}
    </Accordion>
  );
};

export default FaqAccordion;
